import React from "react";
var __jsx = React.createElement;
import { get } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useStoreState } from "pullstate";
import { useAuth } from "@qure/qure-auth";
import { SupportWidget } from "@qureai/support-widget";
import Store from "../utils/Store";
import { getBackendURL } from "../utils/axiosConnect";
import { PatientsContext } from "./Patients/PatientsContext";
import { trackEvent } from "../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../utils/mixpanelEvents";
import { faDownload, faXmark } from "@fortawesome/pro-regular-svg-icons";
import AwesomeIcon from "./AwesomeIcon";
import Toast from "./Toast";
var MENUBAR_ID = "menubar";
var DEFAULT_TOP_OFFSET = 56;
function isPreviewableImageName(name) {
  if (name == null) return false;
  var n = name.toLowerCase().trim();
  return n.endsWith(".png") || n.endsWith(".jpg") || n.endsWith(".jpeg");
}
/** Right-side panel below menubar; `SupportWidget` fills the panel when open. When minimized, only a bottom strip hosts the widget so the rest of the page stays interactive. In `panel` variant it fills its parent container (e.g. an anchored popup) and ignores the global open/minimize state. */
var AppSupportColumn = function AppSupportColumn(_ref) {
  var _get, _userInfo$token, _get2, _get3, _get4, _get5;
  var hideMenu = _ref.hideMenu,
    onlyViewport = _ref.onlyViewport,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "column" : _ref$variant;
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var _useContext = useContext(PatientsContext),
    currentPatient = _useContext.currentPatient;
  var patient_id = (_get = get(currentPatient, ["patient_id"], "")) !== null && _get !== void 0 ? _get : "None";
  var isSupportWidgetOpen = useStoreState(Store, function (s) {
    return s.isSupportWidgetOpen;
  });
  var isSupportWidgetMinimized = useStoreState(Store, function (s) {
    return s.isSupportWidgetMinimized;
  });
  var isDarkMode = useStoreState(Store, function (s) {
    return s.isDarkMode;
  });
  var _useState = useState(DEFAULT_TOP_OFFSET),
    topOffset = _useState[0],
    setTopOffset = _useState[1];
  var _useState2 = useState(false),
    showAttachmentPreview = _useState2[0],
    setShowAttachmentPreview = _useState2[1];
  var _useState3 = useState(null),
    attachmentURL = _useState3[0],
    setAttachmentURL = _useState3[1];
  var _useState4 = useState(null),
    attachmentName = _useState4[0],
    setAttachmentName = _useState4[1];
  var isLoggedIn = get(userInfo, ["authenticated"], false);
  useEffect(function () {
    if (!isSupportWidgetOpen) {
      Store.update(function (s) {
        s.isSupportWidgetMinimized = false;
      });
    }
  }, [isSupportWidgetOpen]);
  useEffect(function () {
    if (!isSupportWidgetOpen || typeof document === "undefined") return;
    var el = document.getElementById(MENUBAR_ID);
    if (el) setTopOffset(el.offsetHeight);
    var observer = new ResizeObserver(function (entries) {
      var entry = entries[0];
      if (entry !== null && entry !== void 0 && entry.target) setTopOffset(entry.target.offsetHeight);
    });
    if (el) observer.observe(el);
    return function () {
      return observer.disconnect();
    };
  }, [isSupportWidgetOpen]);
  var closeAttachmentPreview = function closeAttachmentPreview() {
    trackEvent(MIXPANEL_EVENTS.SUPPORT_WIDGET_ATTACHMENT_PREVIEWED, {
      attachment_name: attachmentName,
      open: false
    });
    setShowAttachmentPreview(false);
    setAttachmentURL(null);
    setAttachmentName(null);
  };
  var downloadAttachment = function downloadAttachment() {
    if (!attachmentURL) return;
    trackEvent(MIXPANEL_EVENTS.SUPPORT_WIDGET_ATTACHMENT_DOWNLOADED, {
      attachment_name: attachmentName,
      attachment_url: attachmentURL
    });
    var filename = attachmentName !== null && attachmentName !== void 0 ? attachmentName : "attachment";
    var a = document.createElement("a");
    a.href = attachmentURL;
    a.download = filename;
    a.rel = "noopener noreferrer";
    try {
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (_unused) {
      Toast.error("Unable to download attachment. Please try again.");
    }
  };
  useEffect(function () {
    if (!showAttachmentPreview || typeof document === "undefined") return;
    var prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    var onKeyDown = function onKeyDown(e) {
      if (e.key === "Escape") {
        closeAttachmentPreview();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return function () {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showAttachmentPreview, closeAttachmentPreview]);
  var isPanel = variant === "panel";
  if (!isLoggedIn || onlyViewport) {
    return null;
  }
  if (!isPanel && (!isSupportWidgetOpen || hideMenu)) {
    return null;
  }
  var apiBaseUrl = getBackendURL() || "http://localhost:8000";
  var authToken = (_userInfo$token = userInfo === null || userInfo === void 0 ? void 0 : userInfo.token) !== null && _userInfo$token !== void 0 ? _userInfo$token : "";
  var id = (_get2 = get(userInfo, ["username"], "")) !== null && _get2 !== void 0 ? _get2 : "";
  var email = (_get3 = get(userInfo, ["details", "email"], "")) !== null && _get3 !== void 0 ? _get3 : "";
  var userName = get(userInfo, ["details", "name"], "") || get(userInfo, ["details", "preferred_username"], "");
  var role = (_get4 = get(userInfo, ["details", "designation"], "")) !== null && _get4 !== void 0 ? _get4 : "";
  var organisation = (_get5 = get(userInfo, ["details", "organisation"], "")) !== null && _get5 !== void 0 ? _get5 : "";
  var handleTicketCreated = function handleTicketCreated(ticketId) {
    trackEvent(MIXPANEL_EVENTS.SUPPORT_WIDGET_TICKET_CREATED, {
      ticket_id: ticketId,
      username: id
    });
  };
  var handleSupportWidgetError = function handleSupportWidgetError(err) {
    var _err$message;
    trackEvent(MIXPANEL_EVENTS.SUPPORT_WIDGET_ERROR, {
      error: (_err$message = err.message) !== null && _err$message !== void 0 ? _err$message : err.name
    });
  };
  var handleMinimizedChange = function handleMinimizedChange(isMinimized) {
    trackEvent(MIXPANEL_EVENTS.SUPPORT_WIDGET_MINIMIZED, {
      is_minimized: isMinimized
    });
    Store.update(function (s) {
      s.isSupportWidgetMinimized = isMinimized;
    });
  };
  var handleAttachmentClicked = function handleAttachmentClicked(att_url, att_name) {
    trackEvent(MIXPANEL_EVENTS.SUPPORT_WIDGET_ATTACHMENT_PREVIEWED, {
      attachment_name: attachmentName,
      open: true
    });
    setAttachmentURL(att_url);
    setAttachmentName(att_name !== null && att_name !== void 0 ? att_name : "");
    setShowAttachmentPreview(true);
  };
  var attachmentPreviewOverlay = showAttachmentPreview && attachmentURL && /*#__PURE__*/React.createElement("div", {
    className: "fixed inset-0 z-[100] flex items-center justify-center bg-sds-blanket p-4 backdrop-blur-sm sm:p-6",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Attachment preview",
    onClick: closeAttachmentPreview
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex h-[min(85vh,800px)] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-sds-base-default bg-sds-base-default shadow-xl",
    onClick: function onClick(e) {
      return e.stopPropagation();
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex shrink-0 items-center justify-end gap-2 border-b border-sds-base-default bg-sds-base-default px-3 py-2"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: downloadAttachment,
    className: "rounded-md p-2 text-sds-base-default transition-colors hover:bg-sds-base-default-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--sds-border-brand-default)]",
    "aria-label": "Download attachment"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faDownload,
    className: "h-5 w-5"
  })), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: closeAttachmentPreview,
    className: "rounded-md p-2 text-sds-base-default transition-colors hover:bg-sds-base-default-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--sds-border-brand-default)]",
    "aria-label": "Close preview"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faXmark,
    className: "h-5 w-5"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "min-h-0 flex-1 overflow-hidden bg-sds-base-secondary p-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex h-full w-full min-h-0 items-center justify-center"
  }, isPreviewableImageName(attachmentName) ?
  /*#__PURE__*/
  /* eslint-disable-next-line @next/next/no-img-element -- attachment preview from support widget URL */
  React.createElement("img", {
    src: attachmentURL,
    alt: "",
    className: "max-h-full max-w-full object-contain"
  }) : /*#__PURE__*/React.createElement("p", {
    className: "max-w-md px-4 text-center text-base text-sds-base-tertiary"
  }, "Preview not supported for this file type.")))));
  var supportWidget = /*#__PURE__*/React.createElement(SupportWidget, {
    apiBaseUrl: apiBaseUrl,
    authToken: authToken,
    user: {
      id: id,
      email: email,
      name: userName,
      role: role,
      organization: organisation
    },
    metadata: {
      platform: "qTrack"
    },
    position: "inline",
    theme: isDarkMode ? "dark" : "light",
    onTicketCreated: handleTicketCreated,
    onError: handleSupportWidgetError,
    onMinimizedChange: handleMinimizedChange,
    onAttachmentClicked: handleAttachmentClicked,
    patient_id: patient_id
  });
  if (isPanel) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, attachmentPreviewOverlay, /*#__PURE__*/React.createElement("div", {
      className: "support-widget-host flex h-full w-full min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain"
    }, supportWidget));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, attachmentPreviewOverlay, /*#__PURE__*/React.createElement("aside", {
    id: "app-support-column",
    className: isSupportWidgetMinimized ? "pointer-events-none fixed right-0 bottom-0 z-[40] flex w-1/4 flex-col items-stretch justify-end" : "fixed right-0 bottom-0 z-[40] flex w-1/4 max-w-md min-w-[280px] min-h-0 flex-col border-l overflow-hidden bg-sds-base-default border-sds-base-default transition-all duration-500 ease-in-out",
    style: isSupportWidgetMinimized ? undefined : {
      top: topOffset
    },
    "aria-label": "Support"
  }, /*#__PURE__*/React.createElement("div", {
    className: isSupportWidgetMinimized ? "support-widget-host pointer-events-auto w-full" : "support-widget-host flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain animate-expandSummary"
  }, supportWidget)));
};
export default AppSupportColumn;